/* eslint-disable @typescript-eslint/no-explicit-any */
import { getNavigationFormat, type NavigationItem } from '../utils/preprocessNavigation'
import { defineStore } from 'pinia'
import { reactive, toRefs, watchEffect } from 'vue'
import { useSocketIO } from '../hooks/useSocketIO'
import { ChatStatus, MessageType, SenderIdType, SenderType, QueueType, ActionType } from '../enum/chatEnum'
import { useLang } from '../hooks/useLang'
import { playSound } from '../hooks/useSound'
import { useI18n } from '../i18n/simple-i18n'
import { getRole, getTenantId, getToken, type WidgetRole } from '../utils/channelStorage'

const BASE_URL = import.meta.env.VITE_API_BASE_URL
const LOCAL = import.meta.env.VITE_API_LOCAL_URL

function getLocalFromURL() {
  // 使用当前页面URL的查询参数
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('local') || 'false'
}

// 每个消息气泡需要拿到的数据
export interface ChatContentType {
  avatar: string
  username: string
  isFirst?: boolean
  isMe?: boolean
  senderId: string
  content: string
  contentType: string
  queuePosition?: number
  sequenceId: string
  chatId: string
  timestamp: string
  sender: string
  duration?: string
  fileSize?: string
  fileName?: string
  isWarn?: boolean
  isBlocked?: boolean
  // 后端下发的消息唯一标识；目前仅 matchQuestions 需要（"解决问题"接口按 messageId 定位）
  messageId?: string
}

export const useChatStore = defineStore('chat-store', () => {
  const state = reactive({
    // 当前身份：'customer' = 访客视角，'agent' = 客服视角。
    // 由入口 index.js 在挂载 Vue 前通过 channelStorage.setRole 写入模块级 role，
    // 此处只是把该值物化到 store 里，方便下游 UI 组件（isMe / 评分卡显隐 / 导航栏显隐等）
    // 从 reactive state 派生。
    role: getRole() as WidgetRole,
    chatId: '0',
    userId: '0',
    chatStatus: ChatStatus.TERMINATION,
    channelId: '0',
    currentAgent: 'agent-0',
    agentAvatar: '',
    chatInfo: [] as ChatContentType[],
    sendDisable: false,
    // 客服视角专用：当前接待的会话已被后端从"服务中队列"移除（QU + SERVING + REMOVE）。
    // 触发后 AgentWindow 会切到 AgentAuthError 页，禁止继续操作。访客侧不消费此字段。
    agentSessionEnded: false,
    // 当前访客的历史会话清单（chatId 列表，新→旧），用于跨会话翻历史
    chatHistory: [] as string[],
    // 当前会话最老一条消息的sequenceId
    lastSequenceId: undefined as string | undefined,
    // 当前历史记录里最老的会话Id
    lastChatId: '0',
    // 访客界面显示的导航栏
    navigation: [] as NavigationItem[],
    // 输入框中的内容
    chatContent: '',
    // 贴底信号：访客主动发送消息（文本/图片/文件）时自增。
    // CustomerContainer 监听其变化后会无视当前粘附态强制贴底，确保”自己发的消息一定可见”。
    scrollToBottomSignal: 0,
    // matchQuestions “解决问题” 按钮的已点击集合，key 为 `${messageId}:${queId}`。
    // 仅当前会话内存，刷新页面重置。
    solvedMatchQuestions: {} as Record<string, true>,
  })

  // 虽然报错了，但是是vscode的bug，实际运行需要，不能删除！！！
  const { t } = useI18n()

  const token = getToken()
  // socket URL 的 channelId 参数取值跟 HTTP Tenant-Id 头保持一致：
  // - 访客：真实的 channelId（access token 的租户边界）
  // - 客服：memberId（客服身份的租户边界，后端 WS 鉴权按此校验）
  // 因此这里用 getTenantId() 而不是 getChannelId()。
  const channel = getTenantId()

  const localParam = getLocalFromURL()

  console.log('chat', localParam)
  const URL = localParam === 'true' ? LOCAL : BASE_URL

  // 客服接入 WS 时后端要求额外带 source=wechat（区分接入渠道）；访客侧不需要。
  const sourceParam = state.role === 'agent' ? '&source=wechat' : ''

  const ws = useSocketIO(
    `${URL}/websocket/socket.io?token=${token}&channelId=${channel}${sourceParam}`,
    // `https://testwww.abot.pro/websocket/socket.io?token=${token}&channelId=${channel}`
  )

  const { getUCLang } = useLang()

  const getAvatar = (r: any) => {
    // 视角翻转：
    // - 访客视角：AGENT 消息用 agentAvatar；CUSTOM 是"我"，走默认 '1'
    // - 客服视角：CUSTOM 消息用客户方头像；AGENT 是"我"，走默认 '1'（或后端下发的 avatar）
    if (state.role === 'agent') {
      switch (r.senderType) {
        case SenderType.AGENT:
          return r.avatar || '1'
        case SenderType.CUSTOM:
          return r.avatar || '1'
        case SenderType.ROBOT:
          return r.avatar || '0'
        case SenderType.SYS:
          return '0'
        default:
          return r.avatar || '1'
      }
    }
    switch (r.senderType) {
      case SenderType.AGENT:
        return state.agentAvatar !== '' ? state.agentAvatar : r.avatar || '1'
      case SenderType.ROBOT:
        return r.avatar || '0'
      case SenderType.SYS:
        return '0'
      case SenderType.CUSTOM:
        return '1'
      default:
        return r.avatar || '1'
    }
  }

  // generateChatFromHistory
  const loadOldChat = (data: any) => {
    console.log('start loading old chat')
    console.log(data)
    // 视角化过滤规则（对齐主 SPA src/stores/modules/session/chat.ts 的 generateChatFromHistory）：
    // - 访客视角：过滤 TO_AGENT（仅给客服看的消息）、GC（评分卡由 socket 单发）、EA（转人工事件）
    // - 客服视角：过滤 TO_CUSTOM（仅给访客看的消息）、RECOM/NAV（推荐卡与导航栏是访客侧交互）、
    //             EA（转人工事件），另外 [TEMP] 跟主 SPA 一致过滤 CC/CD（不 push 到内容区）
    // 客服视角额外一条：非 AGENT 发出的敏感阻断消息不展示，只保留客服自己的阻断记录。
    const isAgent = state.role === 'agent'
    const skipSenderId = isAgent ? SenderIdType.TO_CUSTOM : SenderIdType.TO_AGENT
    const oldChat = data
      .filter((chat: any) => chat.senderId !== skipSenderId)
      .filter((chat: any) => {
        if (isAgent) {
          return (
            chat.messageType !== MessageType.EA &&
            chat.messageType !== MessageType.RECOM &&
            chat.messageType !== MessageType.NAV &&
            chat.messageType !== MessageType.CC &&
            chat.messageType !== MessageType.CD
          )
        }
        return chat.messageType !== MessageType.GC && chat.messageType !== MessageType.EA
      })
      .filter((chat: any) => {
        if (!isAgent) return true
        // 客服视角：仅客服自身的敏感阻断消息保留，其它角色的阻断记录跳过。
        return !(chat.senderType !== SenderType.AGENT && chat.sensitiveAction === 'block')
      })
      .map((r: any) => {
        if (r.messageType === MessageType.WQ) {
          return {
            chatId: r.chatId,
            avatar: getAvatar(r),
            content: r.content.replace(
              '{num}',
              r.queuePosition !== null ? r.queuePosition.toString() : '',
            ),
            contentType: r.contentType,
            sequenceId: r.sequenceId,
            timestamp: r.timestamp,
            sender: r.senderType,
            senderId: r.senderId,
            username: r.senderName ?? 'System',
          }
        } else if (r.messageType === MessageType.NAV) {
          if (state.navigation.length === 0) {
            state.navigation = getNavigationFormat(JSON.parse(r.content), getUCLang())
          }
          return undefined
        } else if (r.messageType === MessageType.RECOM) {
          const temp = JSON.parse(r.content)
          console.log('历史消息：问题卡')
          console.log(temp)
          if (temp.questions.length !== 0) {
            return {
              chatId: r.chatId,
              avatar: getAvatar(r),
              content: r.content,
              contentType: r.messageType, // 特殊处理一下，为了Content里能分辨问题卡
              sequenceId: r.sequenceId,
              timestamp: r.timestamp,
              sender: r.senderType,
              senderId: r.senderId,
              username: r.senderName ?? 'System',
            }
          } else {
            return undefined
          }
        } else if (r.messageType === MessageType.MQ) {
          // matchQuestions：机器人下发的多答案候选。content 是一个 JSON 数组字符串。
          // 空数组直接跳过，避免出现空气泡。
          try {
            const arr = JSON.parse(r.content)
            if (!Array.isArray(arr) || arr.length === 0) return undefined
          } catch {
            return undefined
          }
          return {
            chatId: r.chatId,
            avatar: getAvatar(r),
            content: r.content,
            contentType: r.messageType, // 特殊标记，供 CustomerContent 识别为 MQ
            sequenceId: r.sequenceId,
            timestamp: r.timestamp,
            sender: r.senderType,
            senderId: r.senderId,
            username: r.senderName ?? 'Robot',
            messageId: r.messageId,
          }
        } else {
          // 客服视角下已在上面的 filter 3 里把"非客服自身发的 block 记录"丢掉，
          // 客服自身的 block 记录必须保留（客服要知道自己刚才被拦截了什么）。
          // 访客视角额外把"非客户发的 block"丢掉（客服/机器人/系统的 block 副本对访客侧无意义）。
          if (!isAgent && r.senderType !== SenderType.CUSTOM && r.sensitiveAction === 'block') {
            return undefined
          } else {
            return {
              chatId: r.chatId,
              avatar: getAvatar(r),
              content: r.content,
              contentType: r.contentType,
              sequenceId: r.sequenceId,
              timestamp: r.timestamp,
              sender: r.senderType,
              senderId: r.senderId,
              username: r.senderName ?? 'Robot',
              fileSize: r.fileSize,
              duration: r.duration,
              fileName: r.fileName,
              isWarn: r.sensitiveAction === 'warn',
              isBlocked: r.sensitiveAction === 'block',
            }
          }
        }
      })
      .filter((r: any) => r !== undefined)

    state.chatInfo.unshift(...oldChat.reverse())
  }

  const updateLastChatAndSequence = (data: any) => {
    console.log('開始更新lastChatId和lastSequenceId')
    // 如果消息数少于20条（默认20条），证明该会话消息已经全部加载完
    if (data.length < 20) {
      console.log(state.chatHistory)
      console.log('current', state.lastChatId)
      const lastChat = findLastChatId(state.chatHistory, state.lastChatId)
      console.log('next', lastChat)
      if (lastChat !== null) {
        state.lastChatId = lastChat
        state.lastSequenceId = undefined
      } else {
        // 此处不仅会话消息已经全部加载，并且该会话为最后一个
        state.lastChatId = '0'
        state.lastSequenceId = '0'
        console.log('lastChat === null')
      }
    } else {
      state.lastSequenceId = data[data.length - 1].sequenceId
    }
  }

  const findLastChatId = (chatHistory: string[], currentChatId: string): string | null => {
    const currentIndex = chatHistory.findIndex((chat) => chat === currentChatId)

    if (currentIndex !== -1) {
      // 找到当前聊天，取下一个
      const nextChat = chatHistory[currentIndex + 1]
      return nextChat || null
    } else {
      // 没找到当前聊天，取第一个
      const firstChat = chatHistory[0]
      return firstChat || null
    }
  }

  const wsConnect = async () => {
    try {
      // 重连前确保旧连接已关闭，否则会出现两个 socket 并存
      ws.disconnect()
      console.log('Socket开始连接...', state.channelId)
      await ws.connect()
    } catch (error) {
      console.error('连接失败', error)
    }
  }

  // 客服视角的 WS 消息处理器。跟主 SPA 的 session.ts 消息处理规则对齐：
  // - TEXT/BINARY/EMAIL_SIDE：push 到 chatInfo，senderId===TO_CUSTOM 跳过（仅给访客看的）
  // - SB/SW：按 content 命中已有气泡打敏感标记
  // - ET：客户结束会话 → 关闭输入并断 socket
  // - CC/CD：仅用于状态更新（本单会话面板下暂不 push 到消息区，对齐主 SPA 的 [TEMP] 注释）
  // - 其它（QU/NAV/RECOM/WQ/WS/CI/RC/ER/GC/EA/TT）：单会话面板下无意义，一律忽略
  const handleAgentMessage = (sourceData: any): void => {
    const t = sourceData.messageType
    // 单会话面板过滤：客服可能同时接待多个会话，共享同一条 WS，后端会把所有会话的
    // 增量都推过来；本 widget 只服务 URL 里指定的 chatId，其它会话的消息一律丢弃，
    // 避免误 push 到本会话消息区，或误触本会话的 QU/ET 结束逻辑。
    // 例外：QU 里 chatId 挂在 content.chatId（外层不带），单独在下方 QU 分支里比对。
    if (
      t !== MessageType.QU &&
      sourceData.chatId !== undefined &&
      sourceData.chatId !== state.chatId
    ) {
      return
    }
    if (t === MessageType.TEXT || t === MessageType.BINARY || t === MessageType.EMAIL_SIDE) {
      // 仅给访客看的消息不入客服气泡区
      if (sourceData.senderId === SenderIdType.TO_CUSTOM) return
      playSound('music/message.mp3', 0.5)
      state.chatId = sourceData.chatId
      const thisChat = {
        chatId: sourceData.chatId,
        avatar: getAvatar(sourceData),
        content: sourceData.content,
        contentType: sourceData.contentType,
        sequenceId: sourceData.sequenceId,
        timestamp: sourceData.timestamp,
        sender: sourceData.senderType,
        senderId: sourceData.senderId,
        username:
          sourceData.senderName ??
          (sourceData.senderType === SenderType.ROBOT ? 'Robot' : 'System'),
        fileSize: sourceData.fileSize,
        duration: sourceData.duration,
        fileName:
          sourceData.fileName !== null && sourceData.fileName !== '' ? sourceData.fileName : 'file',
      }
      state.chatInfo.push(thisChat)
      return
    }
    if (t === MessageType.SB) {
      const matched = state.chatInfo.filter((chat) => chat.content === sourceData.content)
      matched.forEach((m) => (m.isBlocked = true))
      return
    }
    if (t === MessageType.SW) {
      const matched = state.chatInfo.filter((chat) => chat.content === sourceData.content)
      matched.forEach((m) => (m.isWarn = true))
      return
    }
    if (t === MessageType.ET) {
      state.chatStatus = ChatStatus.TERMINATION
      state.sendDisable = true
      ws.disconnect()
      return
    }
    // QU（queueUpdate）：后端在 content 里塞 JSON 字符串，客服端只关心
    // queueType=SERVING + action=REMOVE —— 表示当前接待中的这条会话被移出服务队列（结束）。
    // 触发后切到 AgentAuthError 页，其它 queueType/action 组合（ADD/TRANSFER/ASS_*/WAITING/STATUS）
    // 属主 SPA 的多会话调度语义，widget 单会话面板下无意义，全部忽略。
    if (t === MessageType.QU) {
      try {
        const content =
          typeof sourceData.content === 'string'
            ? JSON.parse(sourceData.content)
            : sourceData.content
        // QU 的 chatId 挂在 content 里；不是本会话直接丢，避免误结束当前面板。
        if (content?.chatId !== undefined && content?.chatId !== state.chatId) {
          return
        }
        if (
          content?.queueType === QueueType.SERVING &&
          content?.action === ActionType.REMOVE
        ) {
          state.agentSessionEnded = true
          state.chatStatus = ChatStatus.TERMINATION
          state.sendDisable = true
          ws.disconnect()
        }
      } catch (e) {
        console.warn('[handleAgentMessage] QU content 解析失败:', e)
      }
      return
    }
    // CC/CD 及其它类型：客服单会话面板下暂不处理，避免误 push 系统消息或触发访客侧的排队/评分卡逻辑。
  }

  // 尾部乱序修正：MQ 多实例投递可能出现 RECOM/MQ 先到、TEXT 后到（欢迎语被抢到 RECOM 后面）。
  // 只在 TEXT/BINARY/EMAIL_SIDE 分支使用；回看尾部最多 5 条真 sequenceId 消息，
  // 若自身 seq 更小就 splice 到正确位置。'unknown'（本地未回执）跳过不参与比较。
  // sequenceId 是 19 位雪花 ID，超 Number.MAX_SAFE_INTEGER，必须走 BigInt。
  // 跨会话不比较：只要 prev.chatId 与新消息 chatId 不完全相等就停手（含任一为空的情况）——
  // 本函数只负责“同会话内的乱序修正”，"重新会话"后新会话首条消息如果与旧会话尾条比 seq 大小，
  // 会被误塞进旧消息组中间（服务端 seq 生成并非严格跨会话单调，尤其多实例分片场景）。
  const insertByTailSeq = (chat: ChatContentType) => {
    const seq = chat.sequenceId
    if (!seq || seq === 'unknown') {
      state.chatInfo.push(chat)
      return
    }
    let insertAt = state.chatInfo.length
    for (let i = state.chatInfo.length - 1; i >= 0 && state.chatInfo.length - i <= 5; i--) {
      const prev = state.chatInfo[i]
      // 严格相等比较：只要 chatId 不同（含任一为空）就视为跨会话边界，立刻停手。
      // 之前 `prev.chatId && chat.chatId && !==` 的写法会在任一 chatId 为空/undefined 时短路
      // 不 break，导致新会话消息被塞到旧消息中间。
      if (prev.chatId !== chat.chatId) break
      if (!prev.sequenceId || prev.sequenceId === 'unknown') continue
      try {
        if (BigInt(prev.sequenceId) > BigInt(seq)) {
          insertAt = i
        } else {
          break
        }
      } catch {
        break
      }
    }
    if (insertAt === state.chatInfo.length) state.chatInfo.push(chat)
    else state.chatInfo.splice(insertAt, 0, chat)
  }

  // 监听器只注册一次（store 单例期间复用）。
  // useSocketIO 的 registeredListeners 注册表会在每次 connect() 创建新 socket 时
  // 自动把这些监听器挂到新 socket 上，重连后无需重复注册。
  const handleCustomerMessage = (sourceData: any): void => {
    // chat connect 只有和真人对接才收到
    if (sourceData.messageType === MessageType.CI) {
      console.warn('[ChatInit]disable false text')
      state.sendDisable = false // 对话开始
      state.chatId = sourceData.chatId
    } else if (sourceData.messageType === MessageType.ER) {
      // EventReconnect 重连
      if (state.chatStatus !== ChatStatus.TERMINATION) {
        console.warn('[reconnect]disable false text')
        state.sendDisable = false // 重连
      } else {
        state.sendDisable = true
      }
    } else if (sourceData.messageType === MessageType.RC) {
      console.warn('[RobotConnect]disable false text')
      state.sendDisable = false
      state.chatId = sourceData.chatId
      state.chatStatus = ChatStatus.ROBOT
    } else if (sourceData.messageType === MessageType.CC) {
      console.warn('[ChatConnect]disable false text')
      state.sendDisable = false // 对话开始
      state.chatId = sourceData.chatId
      state.currentAgent = sourceData.caption
      state.chatStatus = ChatStatus.AGENT
      const thisChat = {
        chatId: sourceData.chatId,
        avatar: getAvatar(sourceData),
        content: sourceData.content,
        contentType: sourceData.contentType,
        sequenceId: sourceData.sequenceId,
        timestamp: sourceData.timestamp,
        sender: sourceData.senderType,
        senderId: sourceData.senderId,
        username: sourceData.senderName ?? 'System',
      }
      state.chatInfo.push(thisChat)
    } else if (sourceData.messageType === MessageType.CD) {
      const thisChat = {
        chatId: sourceData.chatId,
        avatar: getAvatar(sourceData),
        content: sourceData.content,
        contentType: sourceData.contentType,
        sequenceId: sourceData.sequenceId,
        timestamp: sourceData.timestamp,
        sender: sourceData.senderType,
        senderId: sourceData.senderId,
        username: sourceData.senderName ?? 'System',
      }
      state.chatInfo.push(thisChat)
      console.warn('trigger CD')
    } else if (
      sourceData.messageType === MessageType.TEXT ||
      sourceData.messageType === MessageType.BINARY ||
      sourceData.messageType === MessageType.EMAIL_SIDE
    ) {
      if (
        sourceData.senderType === SenderType.SYS &&
        sourceData.senderId === SenderIdType.TO_AGENT
      ) {
        return
      }
      // 播放声音
      playSound('music/message.mp3', 0.5)

      state.chatId = sourceData.chatId
      // state.sendDisable = false;
      console.warn('[TEXT]disable false text')
      const thisChat = {
        chatId: sourceData.chatId,
        avatar: getAvatar(sourceData),
        content: sourceData.content,
        contentType: sourceData.contentType,
        sequenceId: sourceData.sequenceId,
        timestamp: sourceData.timestamp,
        sender: sourceData.senderType,
        senderId: sourceData.senderId,
        username:
          sourceData.senderName ??
          (sourceData.senderType === SenderType.ROBOT ? 'Robot' : 'System'),
        fileSize: sourceData.fileSize,
        duration: sourceData.duration,
        fileName:
          sourceData.fileName !== null && sourceData.fileName !== '' ? sourceData.fileName : 'file',
      }
      insertByTailSeq(thisChat)
      console.log('从' + state.currentAgent + '收到消息:' + sourceData.content)
    } else if (sourceData.messageType === MessageType.SB) {
      // 敏感词阻断
      const matchedMessages = state.chatInfo.filter((chat) => chat.content === sourceData.content)
      matchedMessages.forEach((matchedMessage) => {
        matchedMessage.isBlocked = true
      })
      console.warn(`${matchedMessages.length} 条消息被敏感词阻断`)
    } else if (sourceData.messageType === MessageType.SW) {
      // 敏感词警告
      const matchedMessages = state.chatInfo.filter((chat) => chat.content === sourceData.content)
      matchedMessages.forEach((matchedMessage) => {
        matchedMessage.isWarn = true
      })
      console.warn(`${matchedMessages.length} 条消息包含敏感词`)
    } else if (sourceData.messageType === MessageType.NAV) {
      state.navigation = getNavigationFormat(JSON.parse(sourceData.content), getUCLang())
      return undefined
    } else if (sourceData.messageType === MessageType.GC) {
      // 播放声音
      playSound('music/message.mp3', 0.5)
      // 此消息是由system发出的特殊消息：评分卡
      const thisChat = {
        chatId: sourceData.chatId,
        avatar: getAvatar(sourceData),
        content: sourceData.content,
        contentType: sourceData.messageType, // 特殊处理一下，为了Content里能分辨评分卡
        sequenceId: sourceData.sequenceId,
        senderId: sourceData.senderId,
        timestamp: sourceData.timestamp,
        sender: sourceData.senderType,
        username: sourceData.senderName ?? 'System',
      }
      state.chatInfo.push(thisChat)
      console.log('收到评分卡')
    } else if (sourceData.messageType === MessageType.RECOM) {
      const temp = JSON.parse(sourceData.content)
      console.log('message temp')
      console.log(temp)
      // 此消息是由system发出的特殊消息：问题卡
      if (temp.questions.length !== 0) {
        const thisChat = {
          chatId: sourceData.chatId,
          avatar: getAvatar(sourceData),
          content: sourceData.content,
          senderId: sourceData.senderId,
          contentType: sourceData.messageType, // 特殊处理一下，为了Content里能分辨问题卡
          sequenceId: sourceData.sequenceId,
          timestamp: sourceData.timestamp,
          sender: sourceData.senderType,
          username: sourceData.senderName ?? 'System',
        }
        state.chatInfo.push(thisChat)
        console.log('收到问题卡')
      } else {
        return undefined
      }
    } else if (sourceData.messageType === MessageType.MQ) {
      // matchQuestions：机器人下发的多答案候选气泡。
      // content 是形如 [{answer, clicks, question, createBy, createTime}] 的 JSON 数组字符串，
      // 前端存原始字符串，由 MatchQuestionsCard 自行解析、排序、选出主展示答案。
      let arr: unknown = null
      try {
        arr = JSON.parse(sourceData.content)
      } catch (e) {
        console.warn('[MQ] content 解析失败:', e)
        return
      }
      if (!Array.isArray(arr) || arr.length === 0) return
      playSound('music/message.mp3', 0.5)
      state.chatId = sourceData.chatId
      const thisChat = {
        chatId: sourceData.chatId,
        avatar: getAvatar(sourceData),
        content: sourceData.content,
        senderId: sourceData.senderId,
        contentType: sourceData.messageType, // 特殊标记，供 CustomerContent 识别
        sequenceId: sourceData.sequenceId,
        timestamp: sourceData.timestamp,
        sender: sourceData.senderType,
        username: sourceData.senderName ?? 'Robot',
        messageId: sourceData.messageId,
      }
      state.chatInfo.push(thisChat)
      console.log('收到 matchQuestions 卡')
    } else if (sourceData.messageType === MessageType.WS) {
      state.sendDisable = false
      state.chatStatus = ChatStatus.WAITING
    } else if (sourceData.messageType === MessageType.WQ) {
      state.sendDisable = false // 客户进入排队队列，可以输入
      state.chatStatus = ChatStatus.WAITING
      if (sourceData.contentType === 'text/plain') {
        const thisChat = {
          chatId: sourceData.chatId,
          avatar: getAvatar(sourceData),
          content: sourceData.content,
          queuePosition: sourceData.queuePosition,
          contentType: sourceData.contentType,
          sequenceId: sourceData.sequenceId,
          timestamp: sourceData.timestamp,
          senderId: sourceData.senderId,
          sender: sourceData.senderType,
          username: sourceData.senderName ?? 'System',
        }
        state.chatInfo.push(thisChat)
        console.log('从' + state.currentAgent + '收到消息:' + sourceData.content)
      } else if (sourceData.contentType === 'decrease') {
        // 使num - 1
        const thisMessage = state.chatInfo.find(
          (chat): chat is typeof chat & { queuePosition: number } => {
            return chat.chatId === sourceData.chatId && chat.queuePosition !== undefined
          },
        )

        if (thisMessage) {
          thisMessage.queuePosition -= 1 // 现在 TypeScript 知道 queuePosition 是 number
        }
      } else {
        console.warn('Error')
        console.log(sourceData)
        console.log(sourceData.contentType === 'text/plain')
      }
    } else if (sourceData.messageType === MessageType.ET) {
      state.chatStatus = ChatStatus.TERMINATION
      state.sendDisable = true // 客户聊天结束，不能输入
      console.warn('disabled EventTerminate')
      // 直接断开socket
      ws.disconnect()
    }
  }

  ws.on('message', (data) => {
    const sourceData = JSON.parse(data)
    console.log('收到源消息：')
    console.log(sourceData)
    // 客服视角走精简处理器，避免访客侧的排队/评分卡/导航栏等系统消息误压入客服会话区。
    if (state.role === 'agent') {
      handleAgentMessage(sourceData)
    } else {
      handleCustomerMessage(sourceData)
    }
  })

  ws.on('connect_error', (error) => {
    console.error('❌ Socket.IO 连接错误:', error)
  })

  ws.on('connect_timeout', (timeout) => {
    console.error('⏰ Socket.IO 连接超时:', timeout)
  })

  ws.on('error', (error) => {
    console.error('🚨 Socket.IO 错误:', error)
  })

  ws.on('disconnect', (reason) => {
    console.log('🔌 Socket.IO 断开连接:', reason)
  })

  const wsSend = async (query: object) => {
    ws.emit('message', JSON.stringify(query))
  }

  const wsClose = () => {
    ws.disconnect()
  }

  // matchQuestions "解决问题" 按钮的已点击标记（仅当前会话内存）。
  // key 采用 `${messageId}:${queId}`，避免不同消息里同 queId 相互串扰。
  // queId 是 19 位雪花 ID（超 JS Number 精度），全流程按字符串处理。
  const matchQuestionKey = (messageId: string, queId: string) => `${messageId}:${queId}`
  const isMatchQuestionSolved = (messageId: string, queId: string) =>
    Boolean(state.solvedMatchQuestions[matchQuestionKey(messageId, queId)])
  const markMatchQuestionSolved = (messageId: string, queId: string) => {
    state.solvedMatchQuestions[matchQuestionKey(messageId, queId)] = true
  }

  watchEffect(() => {
    console.warn('disable', state.sendDisable)
  })

  return {
    ...toRefs(state),
    wsConnect,
    wsClose,
    wsSend,
    loadOldChat,
    updateLastChatAndSequence,
    isMatchQuestionSolved,
    markMatchQuestionSolved,
    // 暴露 socket.io 的连接状态，供 UI 层判定"切回前台时 WS 是否已断"——
    // 桌面切页签 WS 会一直活，移动端后台冻结超时后 socket.io 会检测到失联。
    wsIsConnected: ws.isConnected,
  }
})
